/* Publishing this tenant's scoring model — how rule results aggregate into a score.
 *
 * **It used to be the last card on the Scores screen and it does not belong there.** The reference
 * art shows Scores as the ledger and the decomposition and nothing else, and it is right: a steward
 * reading yesterday's numbers is not the person changing what a number means. Publishing a version
 * alters the meaning of every score computed under it, which makes it tenant configuration — so it
 * lives beside the other configuration surfaces `/admin` links to, exactly as connectors, alert
 * routing and residency do.
 *
 * **Moved rather than deleted, and that distinction is the whole of this change.** This is the only
 * surface in the product that can publish a scoring model, and `scoring.model.noneGuidance` says in
 * its own words that nothing is scored until one exists. Removing the card to match a screenshot
 * would have removed the capability the Scores screen depends on and left a first-run tenant with no
 * way through the product at all.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { TextField } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Sections } from '@/shared/ui/Layout';

import { useActiveModel, usePublishModel } from '../api/scoring';
import type { Dimension } from '../api/scoring';

const DIMENSION_ORDER: readonly Dimension[] = [
  'completeness',
  'validity',
  'accuracy',
  'consistency',
  'uniqueness',
  'timeliness',
  'integrity',
];

function ModelCard(): ReactElement {
  const { t } = useTranslation();
  const model = useActiveModel();
  const publish = usePublishModel();
  const [edited, setEdited] = useState<Record<string, string>>({});

  const active = model.data;

  /* A 404 here is the expected first-run state, not a failure: no model has been published, and the
   * server's message says exactly that. The primary action lives on this card so the next step is
   * where the problem is reported. */
  if (model.isError) {
    return (
      <Card title={t('scoring.model.title')}>
        <EmptyState
          title={t('scoring.model.none')}
          guidance={t('scoring.model.noneGuidance')}
          action={
            <Button
              tone="primary"
              busy={publish.isPending}
              onClick={() => {
                publish.mutate({ weights: {}, notes: null });
              }}
            >
              {t('scoring.model.publishDefaults')}
            </Button>
          }
        />
        {publish.isError && (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <Alert tone="error">{publish.error.message}</Alert>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card
      title={t('scoring.model.title')}
      hint={
        active === undefined
          ? undefined
          : t('scoring.model.version', { version: active.version })
      }
    >
      <p className="lq-muted">{t('scoring.model.hint')}</p>

      {/* All seven listed, including those weighted zero. A dimension missing from the form would
          make "excluded" and "forgotten" indistinguishable — the same reason the domain refuses a
          partial model. */}
      <div className="lq-grid">
        {DIMENSION_ORDER.map((dimension) => (
          <TextField
            key={dimension}
            label={t(`scoring.dimension.${dimension}`)}
            type="number"
            min={0}
            value={edited[dimension] ?? String(active?.dimension_weights[dimension] ?? 0)}
            onChange={(value) => {
              setEdited((previous) => ({ ...previous, [dimension]: value }));
            }}
            hint={
              dimension === 'accuracy'
                ? /* Said on the screen, not only in the contract. A steward setting accuracy to 5 and
                     seeing it permanently empty would reasonably conclude the product is broken. */
                  t('scoring.model.accuracyNote')
                : undefined
            }
          />
        ))}
      </div>

      <div
        style={{ marginTop: 'var(--space-3)', display: 'flex', gap: 'var(--space-2)' }}
      >
        <Button
          tone="primary"
          busy={publish.isPending}
          onClick={() => {
            const weights: Record<string, number> = {};
            for (const dimension of DIMENSION_ORDER) {
              const raw =
                edited[dimension] ?? String(active?.dimension_weights[dimension] ?? 0);
              const value = Number(raw);
              /* Non-finite input is dropped rather than posted. The server refuses it with a 422
               * either way, but a client that sends `NaN` gets an error about a type instead of about
               * the field somebody was editing. */
              if (Number.isFinite(value)) weights[dimension] = value;
            }
            publish.mutate({ weights, notes: null });
          }}
        >
          {t('scoring.model.publish')}
        </Button>
      </div>

      {publish.isError && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Alert tone="error" title={t('scoring.model.refused')}>
            {publish.error.message}
          </Alert>
        </div>
      )}

      {publish.isSuccess && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          {/* Named as a *new version* rather than "saved". A weight change alters what every future
              score means, and the version number is what makes a trend discontinuity explicable. */}
          <Alert tone="notice">
            {t('scoring.model.published', { version: publish.data.version })}
          </Alert>
        </div>
      )}
    </Card>
  );
}

export function ScoreModelScreen(): ReactElement {
  const { t } = useTranslation();

  return (
    <Sections>
      <PageHeader title={t('scoring.model.title')} lede={t('scoring.model.screenLede')} />
      <ModelCard />
    </Sections>
  );
}
